import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';

interface ReportsExportButtonProps {
  timeRange: '7d' | '30d' | '90d' | 'all';
}

export function ReportsExportButton({ timeRange }: ReportsExportButtonProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleExport = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `/api/reports/export?timeRange=${timeRange}`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to export reports');
      }

      // Get the filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `falcon-reports-${timeRange}-${new Date().toISOString().split('T')[0]}.csv`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Reports exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export reports');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      onClick={handleExport}
      disabled={isLoading}
      variant="secondary"
      size="sm"
      className="gap-2"
    >
      <ArrowDownTrayIcon className="w-4 h-4" />
      {isLoading ? 'Exporting...' : 'Export CSV'}
    </Button>
  );
}
